import { makeAutoObservable, toJS } from 'mobx'
import { CHATBOT_NAME_LIMIT } from 'shared/constants/limits'
import type { IParamsSendFrom } from 'entities/SendFrom'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { AgentActions } from 'entities/AgentActions'
import { ChatbotFields } from './ChatbotField'
import { ChatbotFilters } from './ChatbotFilters'
import { ChatbotSettings } from './ChatbotSettings'
import { ChatbotMessage } from './ChatbotMessage'
import { ChatbotInstructionType, type ChatbotStatus, type IChatbotPayload } from './type'

type IChatbotProps = {
  item?: IChatbotPayload
  entities: IEntitiesStore
}

export class Chatbot {
  static DefaultType = ChatbotInstructionType.Custom

  name = ''
  type: ChatbotInstructionType | null = null
  isActive = false
  sendFrom: null | IParamsSendFrom = null
  fields = new ChatbotFields()
  successActions = new AgentActions()
  fallbackActions = new AgentActions()
  qualifiedActions = new AgentActions()
  disqualifiedActions = new AgentActions()
  handoffActions = new AgentActions()
  firstMessage = new ChatbotMessage()
  settings = new ChatbotSettings()
  filters = new ChatbotFilters()
  entities: IEntitiesStore
  knowledgeBaseDocuments: number[] = []
  status: ChatbotStatus | null = null
  isLiveDocumentIssue = false

  constructor({ item, entities }: IChatbotProps) {
    this.entities = entities
    this.syncOrigin(item)

    makeAutoObservable(this)
  }

  setName = (value: string) => {
    this.name = value.slice(0, CHATBOT_NAME_LIMIT)
  }

  setActive(isActive: boolean) {
    this.isActive = isActive
  }

  setSendFrom(params: IParamsSendFrom) {
    this.sendFrom = params
  }

  setType(type: ChatbotInstructionType) {
    this.type = type
  }

  setKnowledgeBaseDocuments = (documents: number[]) => {
    this.knowledgeBaseDocuments = documents
  }

  syncOrigin(item?: IChatbotPayload | null) {
    this.type =
      item?.type ??
      (this.entities.featureFlagsStore.salesmsg_ai_agent_book_appointment_v2
        ? null
        : Chatbot.DefaultType)
    this.name = item?.name ?? ''
    this.isActive = item?.is_active ?? false
    this.sendFrom = item?.send_from ?? null

    this.firstMessage.syncOrigin(item?.first_message)
    this.settings.syncOrigin(item?.settings)
    this.filters.syncOrigin(item?.filters)
    this.fields.syncOrigin(item?.fields)
    this.successActions.syncOrigin(item?.success_actions)
    this.fallbackActions.syncOrigin(item?.fallback_actions)
    this.qualifiedActions.syncOrigin(item?.qualified_actions)
    this.disqualifiedActions.syncOrigin(item?.disqualified_actions)
    this.handoffActions.syncOrigin(item?.handoff_actions)
    this.knowledgeBaseDocuments = item?.knowledge_base_documents ?? []
    this.status = item?.status ?? null
    this.isLiveDocumentIssue = item?.is_live_document_issue ?? false
  }

  toJSON(): IChatbotPayload {
    return toJS({
      type: this.type as ChatbotInstructionType,
      name: this.name.trim(),
      is_active: this.isActive,
      send_from: this.sendFrom,
      fields: this.fields.toJSON(),
      first_message: this.firstMessage.toJSON(),
      settings: this.settings.toJSON(),
      filters: this.filters.toJSON(),
      success_actions: this.successActions.toJSON(),
      fallback_actions: this.fallbackActions.toJSON(),
      qualified_actions: this.qualifiedActions.toJSON(),
      disqualified_actions: this.disqualifiedActions.toJSON(),
      handoff_actions: this.handoffActions.toJSON(),
      knowledge_base_documents: toJS(this.knowledgeBaseDocuments),
    })
  }

  reset() {
    this.name = ''
    this.isActive = false
    this.sendFrom = null
    this.type = Chatbot.DefaultType
    this.status = null

    this.firstMessage.reset()
    this.settings.reset()
    this.filters.reset()
    this.fields.reset()
    this.successActions.reset()
    this.fallbackActions.reset()
    this.qualifiedActions.reset()
    this.disqualifiedActions.reset()
    this.handoffActions.reset()
    this.knowledgeBaseDocuments = []
    this.isLiveDocumentIssue = false
  }

  get inboxId() {
    if (this.sendFrom?.options.team_id) {
      const inbox = this.entities.inboxesStore.getItem(this.sendFrom?.options.team_id)

      return inbox?.id
    }

    if (this.sendFrom?.options.number_id) {
      const inbox = this.entities.inboxesStore.getInboxByNumber(this.sendFrom?.options.number_id)

      return inbox?.id
    }
  }

  getEnhancePromptData = (prompt: string, agentId: number) => {
    return {
      prompt,
      chatbotType: this.type ?? ChatbotInstructionType.BookAppointment,
      usesKnowledgeBase: this.settings.knowledgeBaseUsage,
      chatbotId: agentId,
    }
  }
}
